import { useEffect, useMemo, useState } from 'react';
import { FiEye } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { selectionProcessesAdmin } from '@/services/selectionProcesses';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ExportButtons from './ExportButtons';

const TABLE_HEAD = ['Protocolo', 'Nome', 'CPF', 'Regional', 'Unidade', 'Data de inscrição', 'Ações'];

const EDUCATION_LEVEL_LABELS = {
  fundamental_2: 'Ensino Fundamental II',
  medio_1: '1º Ano EM',
  medio_2: '2º Ano EM',
  medio_3: '3º Ano EM',
  concluido: 'EM Concluído',
  outro: 'Outro'
};

const SHIFT_LABELS = { morning: 'Manhã', afternoon: 'Tarde', evening: 'Noite' };

function formatAnswer(a) {
  if (a.question_kind === 'checkbox') return Array.isArray(a.value_array) && a.value_array.length > 0 ? a.value_array.join(', ') : '—';
  if (a.question_kind === 'file')
    return a.file_url ? (
      <a
        href={a.file_url}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:underline"
      >
        Abrir arquivo
      </a>
    ) : (
      '—'
    );
  return a.value || '—';
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-dashed border-gray-200 py-2 sm:grid-cols-[180px_1fr]">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      <span className="text-sm text-gray-800">{value || '—'}</span>
    </div>
  );
}

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function RegistrationsTab({ processId, slug }) {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 500);
  const filters = useMemo(() => (debouncedSearch ? { q: debouncedSearch } : {}), [debouncedSearch]);

  useEffect(() => {
    setLoading(true);
    selectionProcessesAdmin
      .registrations(processId, filters)
      .then(res => setItems(res.data || []))
      .finally(() => setLoading(false));
  }, [processId, filters]);

  const openDetail = async reg => {
    setDetail({ id: reg.id, attributes: reg.attributes });
    setDetailLoading(true);
    try {
      const res = await selectionProcessesAdmin.registration(processId, reg.id);
      setDetail(res.data || null);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || JSON.stringify(e)).join('; ') : 'Erro ao carregar inscrição.';
      toast.error(message);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setDetail(null);

  return (
    <div className="tab__pane">
      <div className="filter-bar filter-bar--borderless">
        <div className="filter-bar__row">
          <label
            htmlFor="registrationsSearch"
            className="filter-bar__label"
          >
            Buscar
          </label>
          <input
            id="registrationsSearch"
            name="registrationsSearch"
            type="search"
            aria-label="Buscar inscrito"
            className="form__control form__control--search-with-icon"
            placeholder="Nome, CPF ou protocolo"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-bar__actions u-ml-auto">
          <ExportButtons
            processId={processId}
            slug={slug}
            filters={filters}
          />
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <EmptyState
          type="data"
          text="Nenhum inscrito encontrado"
        />
      ) : (
        <div className="round-dg-wrapper">
          <table className="round-dg round-dg--light">
            <thead className="round-dg__header">
              <tr className="round-dg__row">
                {TABLE_HEAD.map(label => (
                  <th
                    key={label}
                    className="round-dg__cell-header"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="round-dg__body">
              {items.map(r => (
                <tr
                  key={r.id}
                  className="round-dg__row"
                >
                  <th className="round-dg__cell-header round-dg__cell-header--row">{r.attributes.protocol}</th>
                  <td className="round-dg__cell">{r.attributes.full_name}</td>
                  <td className="round-dg__cell">{r.attributes.cpf}</td>
                  <td className="round-dg__cell">{r.attributes.regional_name}</td>
                  <td className="round-dg__cell">{r.attributes.institution_name}</td>
                  <td className="round-dg__cell">{new Date(r.attributes.created_at).toLocaleString('pt-BR')}</td>
                  <td className="round-dg__cell">
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => openDetail(r)}
                    >
                      <ButtonIcon>
                        <FiEye />
                      </ButtonIcon>{' '}
                      Visualizar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={Boolean(detail)}
        onOpenChange={isOpen => {
          if (!isOpen) closeDetail();
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="text-primary-dark">{detail?.attributes?.full_name || 'Inscrição'}</DialogTitle>
            <DialogDescription>Protocolo {detail?.attributes?.protocol}</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <Loader />
          ) : (
            detail?.attributes && (
              <div className="max-h-[60vh] overflow-y-auto pr-2">
                <section className="mb-4">
                  <h3 className="mb-2 text-sm font-bold text-primary-dark">Dados pessoais</h3>
                  <DetailRow
                    label="CPF"
                    value={detail.attributes.cpf}
                  />
                  <DetailRow
                    label="Email"
                    value={detail.attributes.email}
                  />
                  <DetailRow
                    label="Telefone"
                    value={detail.attributes.phone}
                  />
                  <DetailRow
                    label="Data de nascimento"
                    value={detail.attributes.birth_date ? new Date(detail.attributes.birth_date).toLocaleDateString('pt-BR') : null}
                  />
                </section>

                <section className="mb-4">
                  <h3 className="mb-2 text-sm font-bold text-primary-dark">Localização</h3>
                  <DetailRow
                    label="Regional"
                    value={detail.attributes.regional_name}
                  />
                  <DetailRow
                    label="Unidade"
                    value={detail.attributes.institution_name}
                  />
                </section>

                {(detail.attributes.current_education_level || detail.attributes.desired_grade || detail.attributes.preferred_shift) && (
                  <section className="mb-4">
                    <h3 className="mb-2 text-sm font-bold text-primary-dark">Escolaridade</h3>
                    <DetailRow
                      label="Escolaridade atual"
                      value={EDUCATION_LEVEL_LABELS[detail.attributes.current_education_level] || detail.attributes.current_education_level}
                    />
                    <DetailRow
                      label="Série pretendida"
                      value={detail.attributes.desired_grade}
                    />
                    <DetailRow
                      label="Turno preferido"
                      value={SHIFT_LABELS[detail.attributes.preferred_shift] || detail.attributes.preferred_shift}
                    />
                  </section>
                )}

                {detail.attributes.special_needs && (
                  <section className="mb-4">
                    <h3 className="mb-2 text-sm font-bold text-primary-dark">Necessidades especiais</h3>
                    <DetailRow
                      label="Descrição"
                      value={detail.attributes.special_needs_description}
                    />
                  </section>
                )}

                {Array.isArray(detail.attributes.answers) && detail.attributes.answers.length > 0 && (
                  <section>
                    <h3 className="mb-2 text-sm font-bold text-primary-dark">Respostas adicionais</h3>
                    {detail.attributes.answers.map((a, i) => (
                      <DetailRow
                        key={a.question_id ?? i}
                        label={a.question_label || 'Pergunta removida'}
                        value={formatAnswer(a)}
                      />
                    ))}
                  </section>
                )}
              </div>
            )
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDetail}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
